import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@/v2/components/common/Typography'
import Form from '@/v2/modals/edit-patient-basic-info/Form'
import validator from '@/v2/modals/edit-patient-basic-info/validator'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { useUpdatePatient } from '@/v2/hooks/patient/usePatient'
import { toSnakeCase } from '@/v2/utils/format'
import { format } from 'date-fns'
import { Container, Header, Content, Footer } from './styles'

const MobileEditPatientBasicInfo = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { onOpenSnackbar } = useSnackbarContext()

  const { patient, onSuccess } = location.state || {}

  const [values, setValues] = useState({
    firstName: patient?.firstName || '',
    lastName: patient?.lastName || '',
    dob: patient?.dob || null,
    gender: patient?.gender || '',
    ssn: patient?.ssn || '',
    referredBy: patient?.referredBy || '',
  })
  const [error, setError] = useState({})
  const [isSaving, setIsSaving] = useState(false)

  const { updatePatient } = useUpdatePatient(patient?.id)

  const handleBack = () => {
    navigate(-1)
  }

  const handleChange = fields => setValues({ ...values, ...fields })

  const handleSubmit = async () => {
    const validErrors = validator(values)
    setError(validErrors || {})

    if (!validErrors) {
      setIsSaving(true)
      try {
        const formattedDob =
          values.dob === null
            ? null
            : typeof values.dob === 'string'
            ? values.dob
            : format(values.dob, 'yyyy-MM-dd')

        const { error: updateError, ...data } = await updatePatient({
          patient: toSnakeCase({
            id: patient.id,
            fullName: `${values.firstName} ${values.lastName}`,
            ...values,
            dob: formattedDob,
          }),
        })

        if (updateError) {
          onOpenSnackbar('Something went wrong.', 'error')
        } else {
          if (onSuccess) {
            onSuccess(data)
          }
          onOpenSnackbar('Patient has been updated.', 'success')
          navigate(-1)
        }
      } catch (e) {
        console.error('handleSubmit error - edit patient basic info', e)
        onOpenSnackbar('Something went wrong.', 'error')
      } finally {
        setIsSaving(false)
      }
    }
  }

  if (!patient) {
    return (
      <Container>
        <Header>
          <Typography variant="h2" fontSize="16px" fontWeight="500">
            Edit Patient
          </Typography>
          <IconButton onClick={() => navigate(-1)} size="small">
            <CloseIcon />
          </IconButton>
        </Header>
        <Content>
          <Typography textAlign="center">
            Unable to load patient details.
          </Typography>
        </Content>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <Typography variant="h2" fontSize="16px" fontWeight="500">
          Edit Patient
        </Typography>
        <IconButton onClick={handleBack} size="small">
          <CloseIcon />
        </IconButton>
      </Header>

      <Content>
        <Form values={values} error={error} onChange={handleChange} />
      </Content>

      <Footer>
        <Button
          variant="outlined"
          onClick={handleBack}
          disabled={isSaving}
          fullWidth
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSaving}
          fullWidth
        >
          {isSaving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
        </Button>
      </Footer>
    </Container>
  )
}

export default MobileEditPatientBasicInfo
